import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useEffect } from "react";
import { LandingPage } from "./pages/Landing";
import { SearchPage } from "./pages/Search";
import { ProfilePreviewPage } from "./pages/ProfilePreview";
import { AppShell } from "./components/AppShell";
import { AppDashboardPage } from "./pages/AppDashboard";
import { AppProfileDetailPage } from "./pages/AppProfileDetail";
import { AppAccountPage } from "./pages/AppAccount";
import { AppNotificationsPage } from "./pages/AppNotifications";
import { AppAddTargetPage } from "./pages/AppAddTarget";
import { AppAddTargetPreviewPage } from "./pages/AppAddTargetPreview";
import { AppAddTargetProcessingPage } from "./pages/AppAddTargetProcessing";
import { AppBillingPage } from "./pages/AppBilling";
import { PaywallPage } from "./pages/Paywall";
import { PaywallSuccessPage } from "./pages/PaywallSuccess";
import { PrivacyPage } from "./pages/Privacy";
import { TermsPage } from "./pages/Terms";
import { NotFoundPage } from "./pages/NotFound";
import { LoginPage } from "./pages/Login";
import { GuidePage } from "./pages/GuidePage";
import { allGuidePaths } from "./data/guideContent";
import { landingPaths } from "./data/guideAlternates";
import { trackEvent } from "./lib/analytics";
import { ProtectedRoute, PublicOnlyRoute } from "./auth/ProtectedRoute";

// Onboarding pages
import { OnboardingPage } from "./pages/onboarding/Onboarding";
import { OnboardingHowYouHeardPage } from "./pages/onboarding/OnboardingHowYouHeard";
import { OnboardingUsageIntentPage } from "./pages/onboarding/OnboardingUsageIntent";
import { OnboardingTargetPage } from "./pages/onboarding/OnboardingTarget";
import { OnboardingProcessingPage } from "./pages/onboarding/OnboardingProcessing";
import { OnboardingPreviewPage } from "./pages/onboarding/OnboardingPreview";
import { OnboardingReviewPage } from "./pages/onboarding/OnboardingReview";
import { OnboardingNotificationsPage } from "./pages/onboarding/OnboardingNotifications";
import { OnboardingPaywallPage } from "./pages/onboarding/OnboardingPaywall";

function RootPage() {
  const [searchParams] = useSearchParams();
  return searchParams.has("handle") ? <SearchPage /> : <LandingPage />;
}

function RouteSeoGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    const privatePrefixes = [
      "/app",
      "/auth",
      "/onboarding",
      "/paywall",
      "/profile/",
      "/search",
    ];
    const isPrivateOrDynamic = privatePrefixes.some((prefix) =>
      pathname.startsWith(prefix),
    );
    const robots = document.head.querySelector('meta[name="robots"]');

    if (robots) {
      robots.setAttribute(
        "content",
        isPrivateOrDynamic ? "noindex,nofollow" : "index,follow",
      );
    }

    trackEvent("page_view", { route_path: pathname });
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <RouteSeoGuard />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<RootPage />} />
        <Route path={landingPaths.pt} element={<LandingPage />} />
        <Route path={landingPaths.es} element={<LandingPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/profile/:handle" element={<ProfilePreviewPage />} />
        <Route path="/paywall" element={<PaywallPage />} />
        <Route path="/paywall/success" element={<PaywallSuccessPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Localized editorial guides */}
        {allGuidePaths.map((path) => (
          <Route key={path} path={path} element={<GuidePage />} />
        ))}

        {/* Auth routes — redirect to /app if already authenticated */}
        <Route
          path="/auth/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />

        {/* Onboarding routes — require authentication */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/how-you-heard"
          element={
            <ProtectedRoute>
              <OnboardingHowYouHeardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/usage-intent"
          element={
            <ProtectedRoute>
              <OnboardingUsageIntentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/target"
          element={
            <ProtectedRoute>
              <OnboardingTargetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/processing"
          element={
            <ProtectedRoute>
              <OnboardingProcessingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/preview"
          element={
            <ProtectedRoute>
              <OnboardingPreviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/review"
          element={
            <ProtectedRoute>
              <OnboardingReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/notifications"
          element={
            <ProtectedRoute>
              <OnboardingNotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/paywall"
          element={
            <ProtectedRoute>
              <OnboardingPaywallPage />
            </ProtectedRoute>
          }
        />

        {/* Authenticated app routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<AppDashboardPage />} />
          <Route path="add-target" element={<AppAddTargetPage />} />
          <Route
            path="add-target/preview"
            element={<AppAddTargetPreviewPage />}
          />
          <Route
            path="add-target/processing"
            element={<AppAddTargetProcessingPage />}
          />
          <Route
            path="monitor-details/:id"
            element={<AppProfileDetailPage />}
          />
          <Route path="account" element={<AppAccountPage />} />
          <Route path="notifications" element={<AppNotificationsPage />} />
          <Route path="profiles" element={<Navigate to="/app" replace />} />
          <Route path="profiles/:id" element={<AppProfileDetailPage />} />
          <Route path="timeline" element={<Navigate to="/app" replace />} />
          <Route path="billing" element={<AppBillingPage />} />
          <Route path="renew" element={<AppBillingPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
